/**
 * ============================================================================
 * Admin Reference Tables Editor
 * ============================================================================
 * 
 * Page d'administration pour éditer les tables de référence
 * - FE Master (Facteurs d'émission)
 * - Mapping (Liaison activité → facteur)
 * - Jobs (Classification économique)
 * - Geo Units (Classification géographique)
 * 
 * ============================================================================
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import ReferenceTableEditorV2, { ColumnConfig } from "@/components/ReferenceTableEditorV2";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertCircle } from "lucide-react";

// ============================================================================
// COLUMN CONFIGURATIONS
// ============================================================================

const FE_MASTER_COLUMNS: ColumnConfig[] = [
  { key: "id", label: "ID", type: "text", editable: false, width: "w-24" },
  { key: "category", label: "Catégorie", type: "text", editable: true },
  { key: "subcategory", label: "Sous-catégorie", type: "text", editable: true },
  { key: "process_name", label: "Processus", type: "text", editable: true },
  { key: "unit", label: "Unité", type: "text", editable: true },
  { key: "factor_value", label: "Facteur", type: "number", editable: true },
  { key: "scope", label: "Scope", type: "text", editable: true },
  { key: "source_name", label: "Source", type: "text", editable: true },
  { key: "source_year", label: "Année", type: "number", editable: true },
  { key: "region", label: "Région", type: "text", editable: true },
  { key: "explanation_en", label: "Explication (EN)", type: "textarea", editable: true },
  { key: "explanation_fr", label: "Explication (FR)", type: "textarea", editable: true },
  { key: "notes", label: "Notes", type: "textarea", editable: true },
];

const MAPPING_COLUMNS: ColumnConfig[] = [
  { key: "id", label: "ID", type: "text", editable: false, width: "w-24" },
  { key: "job_code", label: "Code Activité", type: "text", editable: true },
  { key: "fe_key", label: "Clé Facteur", type: "text", editable: true },
  { key: "region_code", label: "Code Région", type: "text", editable: true },
  { key: "confidence_score", label: "Confiance", type: "number", editable: true },
  { key: "note_en", label: "Note (EN)", type: "textarea", editable: true },
  { key: "note_fr", label: "Note (FR)", type: "textarea", editable: true },
  { key: "justification_en", label: "Justification (EN)", type: "textarea", editable: true },
  { key: "justification_fr", label: "Justification (FR)", type: "textarea", editable: true },
];

const JOBS_COLUMNS: ColumnConfig[] = [
  { key: "job_code", label: "Code", type: "text", editable: false, width: "w-24" },
  { key: "job_name_en", label: "Nom (EN)", type: "text", editable: true },
  { key: "job_name_fr", label: "Nom (FR)", type: "text", editable: true },
  { key: "job_name_ar", label: "Nom (AR)", type: "text", editable: true },
  { key: "sector_ux", label: "Secteur (EN)", type: "text", editable: true },
  { key: "sector_ux_fr", label: "Secteur (FR)", type: "text", editable: true },
  { key: "subsector", label: "Sous-secteur (EN)", type: "text", editable: true },
  { key: "subsector_fr", label: "Sous-secteur (FR)", type: "text", editable: true },
  { key: "nace_code", label: "Code NACE", type: "text", editable: true },
  { key: "ghg_sector_mapping", label: "Mapping GES", type: "text", editable: true },
];

const GEO_UNITS_COLUMNS: ColumnConfig[] = [
  { key: "country_code", label: "Code Pays", type: "text", editable: false, width: "w-24" },
  { key: "country_name_en", label: "Pays (EN)", type: "text", editable: true },
  { key: "country_name_fr", label: "Pays (FR)", type: "text", editable: true },
  { key: "country_name_ar", label: "Pays (AR)", type: "text", editable: true },
  { key: "region", label: "Région", type: "text", editable: true },
  { key: "subregion", label: "Sous-région", type: "text", editable: true },
];

// ============================================================================
// COMPONENT
// ============================================================================

export default function AdminReferenceTablesEditor() {
  const [activeTab, setActiveTab] = useState("fe_master");

  // Mutation pour mettre à jour une cellule
  const updateCellMutation = trpc.upload.updateCell.useMutation({
    onSuccess: () => {
      toast.success("Cellule mise à jour avec succès");
    },
    onError: (error) => {
      console.error("Error updating cell:", error);
      toast.error(`Erreur: ${error.message}`);
    },
  });

  const handleSaveCell = async (
    rowId: string | number,
    columnName: string,
    newValue: any
  ) => {
    try {
      await updateCellMutation.mutateAsync({
        tableId: activeTab as "fe_master" | "mapping" | "jobs" | "geo_units",
        rowId,
        columnName,
        newValue,
      });
    } catch (error: any) {
      throw error;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Éditer les Tables de Référence</h1>
          <p className="text-muted-foreground mt-2">
            Modifiez les données des fichiers de référence directement dans l'application
          </p>
        </div>

        {/* Warning */}
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <CardTitle className="text-base text-amber-900">Important</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-sm text-amber-800">
            <p>
              Les modifications effectuées ici sont immédiatement sauvegardées dans la base de données.
              Veuillez vérifier vos changements avant de confirmer.
            </p>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="fe_master">FE Master</TabsTrigger>
            <TabsTrigger value="mapping">Mapping</TabsTrigger>
            <TabsTrigger value="jobs">Jobs</TabsTrigger>
            <TabsTrigger value="geo_units">Geo Units</TabsTrigger>
          </TabsList>

          {/* FE Master Tab */}
          <TabsContent value="fe_master" className="space-y-4">
            <ReferenceTableEditorV2
              tableId="fe_master"
              title="FE Master - Facteurs d'Émission"
              description="Éditez les facteurs d'émission par catégorie et scope"
              columns={FE_MASTER_COLUMNS}
              onSave={handleSaveCell}
              pageSize={50}
              searchable={true}
            />
          </TabsContent>

          {/* Mapping Tab */}
          <TabsContent value="mapping" className="space-y-4">
            <ReferenceTableEditorV2
              tableId="mapping"
              title="Mapping - Liaison Activité → Facteur"
              description="Éditez les mappings entre activités et facteurs d'émission"
              columns={MAPPING_COLUMNS}
              onSave={handleSaveCell}
              pageSize={50}
              searchable={true}
            />
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="space-y-4">
            <ReferenceTableEditorV2
              tableId="jobs"
              title="Jobs - Classification Économique"
              description="Éditez la classification des secteurs, sous-secteurs et activités"
              columns={JOBS_COLUMNS}
              onSave={handleSaveCell}
              pageSize={50}
              searchable={true}
            />
          </TabsContent>

          {/* Geo Units Tab */}
          <TabsContent value="geo_units" className="space-y-4">
            <ReferenceTableEditorV2
              tableId="geo_units"
              title="Geo Units - Classification Géographique"
              description="Éditez la classification des pays et régions"
              columns={GEO_UNITS_COLUMNS}
              onSave={handleSaveCell}
              pageSize={50}
              searchable={true}
            />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
